import type { SVGAttributes } from 'react';

import type { Color, ColorName } from '../../styles';
import { getColor } from '../../styles';

/**
 * Shared props for each `*Icon` component in the icon library. When creating an
 * icon, extend its props with this type. Title and description are required for
 * accessibility in each Icon so they are omitted here.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IconProps extends Omit<IconSvgProps, 'iconId'> {}

/**
 * The props for the {@link IconSvg} component. When creating an icon, extends
 * from {@link IconProps} instead.
 */
export interface IconSvgProps extends Omit<SVGAttributes<SVGElement>, 'color'> {
  /** The title of the icon, used as label for accessibility. */
  title?: string;
  /**
   * The description of the icon. Include a description if the title is not
   * enough descriptive.
   */
  description?: string;
  /**
   * Size in pixels – calculated as the height of the icon. Default is `24`.
   *
   * @defaultValue 24px
   */
  size?: number | string;
  /**
   * The color used as base fill for the SVG.
   *
   * @defaultValue 'currentColor'
   */
  baseColor?: Color | ColorName | 'none';
  /**
   * Fill color of the SVG. Can be a CSS color name or a hex color.
   *
   * @deprecated Use `baseColor` instead.
   */
  fill?: string | undefined;
  /** @defaultValue 24px */
  height?: string | number | undefined;
  /** Default `0 0 36 36` */
  viewBox?: string | undefined;
}

/**
 * Replacement for the `svg` HTML element to render an icon in corgi-x. This
 * component implements some attributes for accessibility and adds few basic
 * defaults.
 *
 * @remarks
 * You generally don't use this component directly. Instead, import the icons
 * from the {@link https://corgi-x.tfd.engineering/system/icons | icon library}.
 * @example
 *
 * ```tsx
 * <IconSvg
 *   title="Paw Icon"
 *   description="The shape of a dog's paw."
 *   iconId="paw"
 * >
 *   <path />
 * </IconSvg>;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/iconsvg
 */
export function IconSvg(props: IconSvgProps) {
  const {
    size,
    baseColor = props.fill ?? 'currentColor',
    children,
    height,
    ...svgProps
  } = props;
  return (
    <svg
      height={String(size ?? height ?? 24)}
      viewBox="0 0 36 36"
      role="img"
      {...svgProps}
      fill={getColor(baseColor)}
    >
      {children}
    </svg>
  );
}
